import orderBy from 'lodash/orderBy'
import { SearchResultEntry } from '../controls/Filters'
import {
  ChartEntry,
  EntryType,
  QuestionKind,
  QuestionType,
  Row,
  RowItem
} from '../data/model/chart'
import {
  BranchOption,
  FilterOption,
  FiltersResponse,
  ForkFilter,
  ForkFilterOption,
  FrontendQuestionKind,
  NewFilterOption,
  SearchableFilterOption,
  SelectedFilter,
  WeightingScheme
} from '../data/model/results'
import { chain } from './lodashChain'

export interface WeightingData {
  number?: number
  text: string
}

export const getDefaultFilterOptions = (
  chartEntries: SearchableFilterOption[]
): SearchResultEntry[] => {
  return chartEntries
    .filter(
      (chartEntry) =>
        (chartEntry.questionKind === QuestionKind.Audience &&
          chartEntry.isMultipleChoice === false) ||
        chartEntry.entryType === EntryType.ForkEntryType
    )
    .map((chartEntry) => {
      return { chartEntry }
    })
}

export const getDefaultPublicFilterOptions = (
  chartEntries: SearchableFilterOption[]
): SearchResultEntry[] => {
  return chartEntries
    .filter((chartEntry) => chartEntry.entryType === EntryType.ForkEntryType)
    .map((chartEntry) => {
      return { chartEntry }
    })
}

const getResponseOptions = (entry: SearchResultEntry) => {
  if (entry.isMatrix) {
    const row = entry.chartEntry.rows[entry.matrixRow || 0]
    return row.rowItems
      .map((rowItem) => {
        return {
          id: rowItem.id,
          position: rowItem.position,
          text: rowItem.text,
          completes: rowItem.completes
        }
      })
      .filter((ro) => ro.completes !== 0)
  }
  if (entry.chartEntry.entryType === EntryType.ForkEntryType) {
    return entry.chartEntry.rows.map((row) => {
      return {
        id: row.id,
        position: row.position,
        text: row.text,
        completes: row.rowItems[0].completes
      }
    })
  }
  return entry.chartEntry.rows
    .map((row) => {
      // Sum all "Selected" items instead of just finding the first one
      const totalCompletes = row.rowItems
        .filter((item) => item.text === 'Selected')
        .reduce((sum, item) => sum + item.completes, 0)
      return {
        id: row.id,
        position: row.position,
        text: row.text,
        completes: totalCompletes
      }
    })
    .filter((responseOption) => responseOption.completes)
}

export const getFilterOptions: (
  filterSearchEntries: SearchResultEntry[]
) => FilterOption[] = (filterSearchEntries) => {
  return filterSearchEntries
    .sort((a, b) => a.chartEntry.entryPosition - b.chartEntry.entryPosition)
    .map((question) => {
      const id = question.isMatrix
        ? question.chartEntry.rows[question.matrixRow || 0].id
        : question.chartEntry.id
      const matrixTitleId = question.isMatrix
        ? question.chartEntry.id
        : undefined

      const text = question.isMatrix
        ? `${question.chartEntry.text} ${
            question.chartEntry.rows[question.matrixRow || 0].text
          }`
        : question.chartEntry.text

      return {
        id,
        matrixTitleId,
        text,
        position: question.chartEntry.contextPosition,
        samplesCollected: question.chartEntry.samplesCollected,
        samplesCollectedFiltered: question.chartEntry.samplesCollectedFiltered,
        responseOptions: getResponseOptions(question),
        questionKind: question.chartEntry.questionKind
      }
    })
}

const resolvePublicForkBranchNumber = (branchOption: BranchOption): number => {
  if (/^\d+$/.test(branchOption.id)) {
    return Number.parseInt(branchOption.id, 10)
  }
  return branchOption.position
}

const mapFrontendQuestionKindToQuestionKind = (
  frontendQuestionKind: FrontendQuestionKind
): QuestionKind => {
  switch (frontendQuestionKind) {
    case FrontendQuestionKind.Audience:
      return QuestionKind.Audience
    case FrontendQuestionKind.Questionnaire:
      return QuestionKind.Questionnaire
    case FrontendQuestionKind.None:
      return QuestionKind.None
  }
}

const splitMatrixRowLabel = (
  matrixRowText: string
): { matrixTitle: string; rowText: string } => {
  const separatorIndex = matrixRowText.indexOf(' - ')
  if (separatorIndex === -1) {
    return { matrixTitle: matrixRowText, rowText: matrixRowText }
  }

  return {
    matrixTitle: matrixRowText.slice(0, separatorIndex),
    rowText: matrixRowText.slice(separatorIndex + 3)
  }
}

const mapPublicFilterResponseOptionToRowItem = (
  responseOption: NewFilterOption['responseOptions'][number]
): RowItem => {
  const completes = responseOption.nCompletes ?? 0

  return {
    id: responseOption.id,
    text: responseOption.text,
    position: responseOption.position,
    completes,
    ...(responseOption.nWeightedCompletes != null
      ? { weightedCompletes: responseOption.nWeightedCompletes }
      : {})
  }
}

const mapMatrixRowNativeFilterToRow = (nativeFilter: NewFilterOption): Row => {
  const { rowText } = splitMatrixRowLabel(nativeFilter.text)
  const rowItems = nativeFilter.responseOptions.map((responseOption) => {
    return mapPublicFilterResponseOptionToRowItem(responseOption)
  })
  const samplesCollected = rowItems.reduce(
    (totalCompletes, rowItem) => totalCompletes + rowItem.completes,
    0
  )
  const weightedSamplesCollected = rowItems.reduce(
    (totalWeightedCompletes, rowItem) =>
      totalWeightedCompletes + (rowItem.weightedCompletes ?? rowItem.completes),
    0
  )

  return {
    id: nativeFilter.questionId,
    text: rowText,
    position: nativeFilter.contextPosition,
    samplesCollected,
    samplesCollectedFiltered: samplesCollected,
    weightedSamplesCollected: weightedSamplesCollected,
    weightedSamplesCollectedFiltered: weightedSamplesCollected,
    rowItems
  }
}

const mapGroupedMatrixNativeFiltersToSearchable = (
  matrixTitleId: string,
  matrixRowFilters: NewFilterOption[]
): SearchableFilterOption => {
  const sortedMatrixRowFilters = [...matrixRowFilters].sort(
    (leftFilter, rightFilter) =>
      leftFilter.entryPosition - rightFilter.entryPosition
  )
  const firstMatrixRowFilter = sortedMatrixRowFilters[0]
  const { matrixTitle } = splitMatrixRowLabel(firstMatrixRowFilter.text)
  const minimumEntryPosition = sortedMatrixRowFilters.reduce(
    (minimumPosition, nativeFilter) =>
      Math.min(minimumPosition, nativeFilter.entryPosition),
    sortedMatrixRowFilters[0].entryPosition
  )

  return {
    id: matrixTitleId,
    text: matrixTitle,
    entryPosition: minimumEntryPosition,
    contextPosition:
      firstMatrixRowFilter.matrixContextPosition ??
      firstMatrixRowFilter.contextPosition,
    questionType: QuestionType.matrix,
    questionKind: mapFrontendQuestionKindToQuestionKind(
      firstMatrixRowFilter.questionKind
    ),
    entryType: EntryType.MatrixEntryType,
    isMultipleChoice: false,
    samplesWanted: 0,
    samplesCollected: 0,
    samplesCollectedFiltered: 0,
    rows: sortedMatrixRowFilters.map((nativeFilter) => {
      return mapMatrixRowNativeFilterToRow(nativeFilter)
    })
  }
}

const mapNativeQuestionFiltersToSearchable = (
  nativeFilters: NewFilterOption[]
): SearchableFilterOption[] => {
  const nonMatrixFilters = nativeFilters.filter(
    (nativeFilter) => nativeFilter.matrixTitleId == null
  )
  const matrixRowFilters = nativeFilters.filter(
    (nativeFilter) => nativeFilter.matrixTitleId != null
  )

  const matrixTitleIds = [
    ...new Set(
      matrixRowFilters.map(
        (nativeFilter) => nativeFilter.matrixTitleId as string
      )
    )
  ]

  const matrixFilterOptions = matrixTitleIds.map((matrixTitleId) => {
    const matrixRowsForTitle = matrixRowFilters.filter(
      (nativeFilter) => nativeFilter.matrixTitleId === matrixTitleId
    )
    return mapGroupedMatrixNativeFiltersToSearchable(
      matrixTitleId,
      matrixRowsForTitle
    )
  })

  const nonMatrixFilterOptions = nonMatrixFilters.map((nativeFilter) => {
    return mapPublicFilterOptionToSearchable(nativeFilter)
  })

  return [...nonMatrixFilterOptions, ...matrixFilterOptions]
}

export const mapPublicNativeForkFiltersToForkFilters = (
  forkFilters: ForkFilterOption[]
): ForkFilter[] => {
  return forkFilters.map((forkFilter) => {
    return {
      forkId: forkFilter.id,
      forkName: forkFilter.text,
      branches: forkFilter.responseOptions.map((branchOption) => {
        return {
          branchNumber: resolvePublicForkBranchNumber(branchOption),
          branchLabel: branchOption.text
        }
      })
    }
  })
}

const mapPublicFilterResponseOptionToRow = (
  responseOption: NewFilterOption['responseOptions'][number]
): Row => {
  const completes = responseOption.nCompletes ?? 0
  const weightedSamplesCollected =
    responseOption.nWeightedCompletes != null
      ? responseOption.nWeightedCompletes
      : completes

  return {
    id: responseOption.id,
    text: responseOption.text,
    position: responseOption.position,
    samplesCollected: completes,
    samplesCollectedFiltered: completes,
    weightedSamplesCollected: weightedSamplesCollected,
    weightedSamplesCollectedFiltered: weightedSamplesCollected,
    rowItems: [
      {
        id: responseOption.id,
        text: 'Selected',
        position: responseOption.position,
        completes,
        ...(responseOption.nWeightedCompletes != null
          ? { weightedCompletes: responseOption.nWeightedCompletes }
          : {})
      }
    ]
  }
}

const mapPublicFilterOptionToSearchable = (
  nativeFilter: NewFilterOption
): SearchableFilterOption => {
  const matrixTitleId = nativeFilter.matrixTitleId

  return {
    id: matrixTitleId ?? nativeFilter.questionId,
    text: nativeFilter.text,
    entryPosition: nativeFilter.entryPosition,
    contextPosition: nativeFilter.contextPosition,
    questionType: nativeFilter.questionType,
    questionKind: mapFrontendQuestionKindToQuestionKind(
      nativeFilter.questionKind
    ),
    entryType: matrixTitleId
      ? EntryType.MatrixEntryType
      : EntryType.QuestionEntryType,
    isMultipleChoice: false,
    samplesWanted: 0,
    samplesCollected: 0,
    samplesCollectedFiltered: 0,
    rows: nativeFilter.responseOptions.map((responseOption) => {
      return mapPublicFilterResponseOptionToRow(responseOption)
    })
  }
}

export const mapPublicFilters = (
  native: FiltersResponse
): SearchableFilterOption[] => {
  const forkFilterOptions = transformForkDataIntoSearchableFilterOption(
    mapPublicNativeForkFiltersToForkFilters(native.forkFilters)
  )
  const questionFilterOptions = mapNativeQuestionFiltersToSearchable(
    native.filters
  )

  return [...forkFilterOptions, ...questionFilterOptions]
}

export const transformForkDataIntoSearchableFilterOption = (
  forkFilters: ForkFilter[]
): SearchableFilterOption[] => {
  return forkFilters.map((forkFilter, index) => {
    return {
      id: forkFilter.forkId,
      text: forkFilter.forkName,
      entryPosition: index - 100, // this is because the entry position for forks is coming from the draftQuestionnaire so it will overlap with some question
      contextPosition: index + 1, // the contextPosition in the draftQuestionnaire is 0 based while the one in the summary is 1 based
      entryType: EntryType.ForkEntryType,
      questionType: QuestionType.none,
      questionKind: QuestionKind.None,
      isMultipleChoice: false,
      samplesWanted: 0, // forks don't show these numbers in the  UI so we don't need them
      samplesCollected: 0,
      samplesCollectedFiltered: 0,
      rows: forkFilter.branches
        .sort((a, b) => a.branchNumber - b.branchNumber)
        .map((branch, index) => {
          return {
            id: branch.branchNumber.toString(),
            text: branch.branchLabel,
            position: index,
            samplesCollected: 0,
            samplesCollectedFiltered: 0,
            weightedSamplesCollected: 0,
            weightedSamplesCollectedFiltered: 0,
            rowItems: [
              {
                id: forkFilter.forkId,
                text: branch.branchLabel,
                position: index,
                completes: 0
              }
            ]
          }
        })
    }
  })
}

export const constructSearchableFilterOptions = (
  allFilterOptions: SearchableFilterOption[]
): SearchableFilterOption[] => {
  return chain(allFilterOptions)
    .filter(
      (chartEntry) =>
        chartEntry.questionKind === QuestionKind.Audience ||
        chartEntry.questionType === QuestionType.basic ||
        chartEntry.questionType === QuestionType.matrix ||
        chartEntry.entryType === EntryType.ForkEntryType
    )
    .sortBy((chartEntry) => chartEntry.entryPosition)
    .value()
}

export const orderSelectedFilterOptions = (
  selectedFilterOptions: SearchResultEntry[]
): FilterOption[] => {
  return orderBy(
    getFilterOptions(selectedFilterOptions),
    'chartEntry.questionKind',
    'desc'
  )
}

/**
 * Shapes the panel's selections into the API's `responseFilters`, merging any
 * that share a questionId.
 *
 * The backend turns every entry into its own join, so entries are ANDed while
 * the option ids within one are ORed. The combined view can hold two selections
 * for one questionId — a market-split cluster shows Region NL and Region BE as
 * separate cards — and sending those as two entries would ask for respondents
 * who chose a Dutch *and* a Belgian region, which nobody has, emptying the
 * dashboard. Unioning their option ids asks the intended question instead:
 * option ids are unique to a market, so each market matches only its own.
 *
 * Selections with no options ticked are dropped; they restrict nothing and the
 * backend discards them anyway.
 */
export const toResponseFilters = (
  selectedFilters: SelectedFilter[] | undefined
): { questionId: string; responseOptionIds: string[] }[] => {
  const optionIdsByQuestionId = new Map<string, string[]>()

  for (const filter of selectedFilters ?? []) {
    if (!filter.questionId || !filter.responseOptionIds?.length) continue

    const existing = optionIdsByQuestionId.get(filter.questionId) ?? []
    optionIdsByQuestionId.set(filter.questionId, [
      ...existing,
      ...filter.responseOptionIds.filter(
        (optionId) => !existing.includes(optionId)
      )
    ])
  }

  return [...optionIdsByQuestionId.entries()].map(
    ([questionId, responseOptionIds]) => ({ questionId, responseOptionIds })
  )
}

export const getSelectedFilterCount = (
  selectedFilters: SelectedFilter[]
): number => {
  return selectedFilters
    .map(
      (selectedFilter) =>
        selectedFilter.branchNumbers?.length ||
        0 + (selectedFilter.responseOptionIds?.length || 0)
    )
    .reduce((acc, num) => acc + num, 0)
}

export const shouldShowWeightedChartData = (
  weightingScheme: WeightingScheme | undefined | null
): boolean => {
  return weightingScheme != null
}

export const getWeightingQuestionsForDisplay = (
  weightingScheme: WeightingScheme | undefined,
  summaryData: ChartEntry[]
): WeightingData[] | undefined => {
  if (weightingScheme == null) {
    return undefined
  }

  if (weightingScheme.isCustomWeighting) {
    return [{ text: 'Custom weighting scheme' }]
  }

  const questionIds = weightingScheme.questions
  if (questionIds == null) {
    return undefined
  }

  return questionIds
    .map((questionId) => {
      return summaryData.find((chartEntry) => chartEntry.id === questionId)
    })
    .filter((question): question is ChartEntry => question != null)
    .map((question) => {
      return {
        number: question.contextPosition,
        text: question.text
      }
    })
}

export const replaceCompletesOnWeightedData = (
  chartEntry: ChartEntry,
  showWeightedData: boolean
): ChartEntry => {
  if (!showWeightedData) {
    return chartEntry
  }

  return {
    ...chartEntry,
    samplesCollected:
      chartEntry.weightedSamplesCollected !== undefined
        ? chartEntry.weightedSamplesCollected
        : chartEntry.samplesCollected,
    samplesCollectedFiltered:
      chartEntry.weightedSamplesCollectedFiltered !== undefined
        ? chartEntry.weightedSamplesCollectedFiltered
        : chartEntry.samplesCollectedFiltered,
    rows: chartEntry.rows.map((row) => {
      return {
        ...row,
        samplesCollected:
          // @todo Legacy eslint violation – fix this when editing
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          row.weightedSamplesCollected !== undefined
            ? row.weightedSamplesCollected
            : row.samplesCollected,
        samplesCollectedFiltered:
          // @todo Legacy eslint violation – fix this when editing
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          row.weightedSamplesCollectedFiltered !== undefined
            ? row.weightedSamplesCollectedFiltered
            : row.samplesCollectedFiltered,
        rowItems: row.rowItems.map((rowItem) => {
          return {
            ...rowItem,
            completes:
              rowItem.weightedCompletes !== undefined
                ? rowItem.weightedCompletes
                : rowItem.completes
          }
        })
      }
    })
  }
}

export const transformWeightedQuestionsToTooltipMarkup = (
  weightingScheme?: WeightingData[]
): JSX.Element => {
  return (
    <>
      <b>Weighting on:</b> <br />
      {weightingScheme?.map((ws) => (
        <>
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {ws?.number ? `A${ws.number} ${ws.text}` : ws.text} <br />
        </>
      ))}
    </>
  )
}

export const matrixHasDifferentRowCounts = (entry: ChartEntry): boolean => {
  if (entry.rows.length === 0) {
    return false
  }

  const firstRowCount = entry.rows[0].samplesCollectedFiltered

  return entry.rows.some(
    (row) => row.samplesCollectedFiltered !== firstRowCount
  )
}
